<!-- mic-modal is always present in DOM and activated via open() -->
<mic-modal #modalRef (result)="onModalClosed()"></mic-modal>

<!-- Modal content template passed to mic-modal.open() -->
<ng-template #modalContent>
  <form [formGroup]="bulkWOForm">
    <div class="flex flex-col mt-0 mb-1 gap-1">
      <div class="flex items-center justify-between">
        <h2 class="mt-0 mb-0">
          {{
            isEditMode ? translationService.translate('editBulkWorkOrderText') : translationService.translate('addBulkWorkOrderLineText')
          }}
        </h2>
      </div>
      <div class="flex justify-end">
        <mic-status class="primary whitespace-nowrap">* {{ translationService.translate('requiredFieldsText') }}</mic-status>
      </div>
    </div>

    <div class="form-grid form-grid-3">
      <mat-divider class="col-span-3"></mat-divider>

      <!-- Row 1: First Barcode, Production Customer, Number of Lines -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full">
            <mat-label>{{ translationService.translate('barCodeNumberText') }}</mat-label>
            <input
              matInput
              [formControlName]="'firstBarcode'"
              autocomplete="off"
              maxlength="10"
              (blur)="onFirstBarcodeBlur()"
              [matTooltip]="computedBarcodesTooltip"
              matTooltipPosition="below"
              [matTooltipDisabled]="computedBarcodes.length === 0"
            />
            @if (barcodeValidationLoading) {
            <div matSuffix class="flex items-center">
              <mat-spinner diameter="16"></mat-spinner>
            </div>
            } @if (submittedBulk && bulkWOForm.get('firstBarcode')?.hasError('required')) {
            <mat-error>{{ translationService.translate('firstBarCodeRequiredText') }}</mat-error>
            } @if ((submittedBulk || bulkWOForm.get('firstBarcode')?.touched) && bulkWOForm.get('firstBarcode')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max10CharactersText') }}</mat-error>
            } @if ((submittedBulk || bulkWOForm.get('firstBarcode')?.touched) && bulkWOForm.get('firstBarcode')?.hasError('barcode')) {
            <mat-error>{{ translationService.translate('onlyLettersAndNumbersText') }}</mat-error>
            } @if (submittedBulk && bulkWOForm.get('firstBarcode')?.hasError('barcodeOversize')) {
            <mat-error>{{ translationService.translate('barcodeOversizeText') }}</mat-error>
            } @if (bulkWOForm.get('firstBarcode')?.hasError('barcodeExistsLocal')) {
            <mat-error>{{ duplicateBarcodeCount }} {{ translationService.translate('barcodesAlreadyAddedText') }}</mat-error>
            } @if (bulkWOForm.get('firstBarcode')?.hasError('barcodeExists')) {
            <mat-error>{{ duplicateBarcodeCount }} {{ translationService.translate('barcodeExistsSystemText') }}</mat-error>
            }
          </mat-form-field>
        </div>

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('productionCustomerText') }}"
            requiredLabel="{{ translationService.translate('productionCustomerRequiredText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ translationService.translate('selectProductionCustomerText') }}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'productionCustomerId'"
            [data]="productionCustomers"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full">
            <mat-label>{{ translationService.translate('numberOfLinesText') }}</mat-label>
            <input
              matInput
              [formControlName]="'numberOfLines'"
              autocomplete="off"
              type="number"
              min="1"
              max="999"
              step="1"
              (keydown)="onNumberOfLinesKeydown($event)"
            />
            @if ((submittedBulk || bulkWOForm.get('numberOfLines')?.touched) && bulkWOForm.get('numberOfLines')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if ((submittedBulk || bulkWOForm.get('numberOfLines')?.touched) && bulkWOForm.get('numberOfLines')?.hasError('integer')) {
            <mat-error>{{ translationService.translate('wholeNumbersOnlyText') }}</mat-error>
            } @if ((submittedBulk || bulkWOForm.get('numberOfLines')?.touched) && bulkWOForm.get('numberOfLines')?.hasError('min')) {
            <mat-error>{{ translationService.translate('minimum1Text') }}</mat-error>
            } @if ((submittedBulk || bulkWOForm.get('numberOfLines')?.touched) && bulkWOForm.get('numberOfLines')?.hasError('max')) {
            <mat-error>{{ translationService.translate('maximum999Text') }}</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <mat-divider class="col-span-3"></mat-divider>

      <!-- Row 2: Process Code, Make, Series -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('casingProcessCodeText') }}"
              requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('casingProcessCodeText')}`}}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'processCodeId'"
              [data]="bulkProcessCodes"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (processCodesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('makeText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectMakeText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'makeId'"
              [data]="makes"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (makesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('seriesText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectSeriesText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'seriesId'"
              [data]="series"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (seriesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>
      </div>

      <mat-divider class="col-span-3"></mat-divider>

      <!-- Row 3: Size, Mold (conditional), Tread Design -->
      <div
        class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 gap-3 items-start"
        [ngClass]="showMoldRadio ? 'lg-grid-cols-3 xl-grid-cols-3' : 'lg-grid-cols-2 xl-grid-cols-2'"
      >
        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('sizeText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectSizeText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'sizeId'"
              [data]="sizes"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (sizesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        @if (showMoldRadio) {
        <div class="grid-item">
          <mat-radio-group [formControlName]="'moldType'" class="mold-type-inline">
            <mat-radio-button value="P">{{ translationService.translate('preMoldText') }}</mat-radio-button>
            <mat-radio-button value="M">{{ translationService.translate('customMoldText') }}</mat-radio-button>
          </mat-radio-group>
        </div>
        }

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('treadDesignText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectTreadText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'treadId'"
              [data]="treads"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (treadsLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>
      </div>

      <!-- NTR + MFG DOT + Brand Number in isolated sub-grid with items-start so warnings
           below one field do not stretch the adjacent cells in the same grid row -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        <div class="grid-item">
          <mat-form-field class="w-full" subscriptSizing="dynamic">
            <mat-label>{{ translationService.translate('numberOfTimesRetreadedText') }}</mat-label>
            <input
              matInput
              [formControlName]="'ntrId'"
              placeholder="{{`${translationService.translate('enterMfgDotText').split(' ')[0]} ${translationService.translate('ntrText')}`}}"
              maxlength="1"
              autocomplete="off"
            />
            @if (ntrCamValidating) {
            <mat-spinner matSuffix diameter="16"></mat-spinner>
            } @if (submittedBulk && bulkWOForm.get('ntrId')?.hasError('ntrDigit')) {
            <mat-error>{{ translationService.translate('ntrDigitErrorText') }}</mat-error>
            }
          </mat-form-field>
          @if (camNtrError) {
          <div class="cam-warning">
            <span class="cam-warning-icon">&#9888;</span>
            <span>{{ camNtrError }}</span>
          </div>
          }
        </div>

        <div class="grid-item">
          <mat-form-field class="w-full" subscriptSizing="dynamic" floatLabel="always">
            <mat-label>{{ translationService.translate('mfgDotText') }}</mat-label>
            <input matInput formControlName="mfgDot" placeholder="{{ translationService.translate('enterMfgDotText') }}" maxlength="4" />
            @if (mdotCamValidating) {
            <mat-spinner matSuffix diameter="16"></mat-spinner>
            } @if (submittedBulk && bulkWOForm.get('mfgDot')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max4CharactersText') }}</mat-error>
            } @if (submittedBulk && bulkWOForm.get('mfgDot')?.hasError('alnum')) {
            <mat-error>{{ translationService.translate('onlyLettersAndNumbersText') }}</mat-error>
            } @if (bulkWOForm.get('mfgDot')?.hasError('mfgDotFormat')) {
            <mat-error
              >{{ translationService.translate('mfgDotFormatInvalidText') }} —
              {{ bulkWOForm.get('mfgDot')?.getError('mfgDotFormat')?.hint }}</mat-error
            >
            }
          </mat-form-field>
          @if (camMdotError) {
          <div class="cam-warning">
            <span class="cam-warning-icon">&#9888;</span>
            <span>{{ camMdotError }}</span>
          </div>
          }
        </div>

        <div class="grid-item">
          <mat-form-field class="w-full" subscriptSizing="dynamic" floatLabel="always">
            <mat-label>{{ translationService.translate('brandText') }}</mat-label>
            <input
              matInput
              [formControlName]="'brandNumber'"
              placeholder="{{ translationService.translate('enterBrandNumberText') }}"
              maxlength="25"
              (input)="onBrandNumberInput($event)"
            />
            @if (submittedBulk && bulkWOForm.get('brandNumber')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max25CharactersText') }}</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <div class="col-span-3">
        <mat-form-field class="w-full" subscriptSizing="dynamic" floatLabel="always">
          <mat-label>{{ translationService.translate('casingNotesText') }}</mat-label>
          <textarea
            matInput
            rows="3"
            class="casing-notes-textarea"
            formControlName="casingNotes"
            placeholder="{{`${translationService.translate('enterMfgDotText').split(' ')[0]} ${translationService.translate('casingNotesText')}`}}"
          ></textarea>
          @if (submittedBulk && bulkWOForm.get('casingNotes')?.hasError('maxlength')) {
          <mat-error>{{ translationService.translate('max2000CharactersText') }}</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- Edit-mode validation warnings: shown when a header field change (plant / customer)
           has made a previously stored value unavailable. Fields are cleared so form
           validation will block save until the user re-selects a valid value. -->
      @if (bulkErrors.length > 0) {
      <div class="col-span-3">
        @for (err of bulkErrors; track err) {
        <div class="cam-warning mb-1">
          <span class="cam-warning-icon">&#9888;</span>
          <span>{{ err }}</span>
        </div>
        }
      </div>
      }

      <mat-divider class="col-span-3"></mat-divider>

      <div class="col-span-3 flex justify-end items-center">
        <div class="flex flex-row gap-2">
          <button mat-stroked-button class="w-auto" color="primary" (click)="onCancelBulk()" type="button">
            {{ translationService.translate('cancelText') }}
          </button>
          <button mat-flat-button class="w-auto" color="primary" (click)="addBulkWOLine()" [disabled]="isAddDisabled" type="button">
            {{ isEditMode ? translationService.translate('updateWOLinesText') : translationService.translate('addLinesText') }}
          </button>
        </div>
      </div>
    </div>
  </form>
</ng-template>
